/**
 * Centralized badge style definitions for metadata chips
 *
 * Uses semantic CSS variables from globals.css and tailwind.config.ts
 * to ensure consistent styling across all metadata components.
 *
 * Usage:
 *   import { BADGE_STYLES } from '@/lib/utils/metadataBadgeStyles';
 *   <Badge className={BADGE_STYLES.judge}>Judge Name</Badge>
 */

/**
 * Badge style constants for different metadata types
 * Each uses the semantic badge-* CSS variables defined in the design system
 */
export const BADGE_STYLES = {
  /** Teal - for judges and senate presidents */
  judge:
    'text-sm font-medium bg-badge-judge-bg text-badge-judge-text border-badge-judge-border hover:bg-badge-judge-bg-hover transition-colors px-3 py-1',

  /** Amber - for courts */
  court:
    'text-sm font-medium bg-badge-court-bg text-badge-court-text border-badge-court-border hover:bg-badge-court-bg-hover transition-colors px-3 py-1',

  /** Cyan - for legal areas */
  area: 'text-sm font-medium bg-badge-area-bg text-badge-area-text border-badge-area-border hover:bg-badge-area-bg-hover transition-colors px-3 py-1',

  /** Blue - for legislation documents and types */
  legislation:
    'text-sm font-medium bg-badge-legislation-bg text-badge-legislation-text border-badge-legislation-border hover:bg-badge-legislation-bg-hover transition-colors px-3 py-1',

  /** Purple - for decision/court decisions */
  decision:
    'text-sm font-medium bg-badge-decision-bg text-badge-decision-text border-badge-decision-border hover:bg-badge-decision-bg-hover transition-colors px-3 py-1',

  /** Gray - for neutral/generic metadata (subareas, natures, etc.) */
  neutral:
    'text-sm font-medium bg-badge-neutral-bg text-badge-neutral-text border-badge-neutral-border hover:bg-badge-neutral-bg-hover transition-colors px-3 py-1',
} as const;

/**
 * Shared metadata sidebar layout styles
 * Used by both LegislationMetadata and DecisionMetadata for consistent appearance
 */
export const SIDEBAR_STYLES = {
  /** Main container wrapper */
  container: 'p-4',

  /** Header section with title and badges */
  header: 'pb-3 space-y-2',

  /** Title row with inline badges */
  titleRow: 'flex items-center gap-2 flex-wrap',

  /** Main title (regulation number, case file number) */
  title:
    'text-2xl font-semibold text-content-primary leading-tight break-words',

  /** Subtitle/description text */
  description: 'text-base text-content-primary leading-snug',

  /** Main content sections container */
  sectionsContainer: 'space-y-5 pt-2',

  /** Individual section wrapper */
  section: 'space-y-3',

  /** Section label/heading */
  sectionLabel: 'text-base font-semibold text-content-primary',

  /** Sub-label for nested items */
  subLabel: 'text-sm font-semibold text-content-secondary',

  /** Simple label-value item */
  itemContainer: 'space-y-1',
  itemLabel: 'text-base font-semibold text-content-primary',
  itemValue: 'text-base text-content-secondary',

  /** Badge list container */
  badgeList: 'flex flex-wrap gap-2',

  /** Date table styles */
  dateTable: 'border-2 border-line-strong rounded-lg overflow-hidden',
  dateTableInner: 'divide-y-2 divide-line-strong',
  dateRow: 'grid grid-cols-2',
  dateLabelCell:
    'px-3 py-2 bg-surface-subtle text-base font-semibold text-content-primary',
  dateValueCell: 'px-3 py-2 text-base text-content-primary',
} as const;
